import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { EditorProvider } from './contexts/EditorContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider, useLanguage } from './i18n';
import { supabase } from './lib/supabase';
import { loadAndCacheUserFonts, injectCachedFonts, getCachedFonts } from './utils/fontCache';
import './App.css';
import HomePage from './pages/HomePage';
import Dashboard from './pages/Dashboard';
import AccountPage from './pages/AccountPage';
import AdminPage from './pages/AdminPage';
import EditorPage from './pages/EditorPage';
import RunnerPage from './pages/RunnerPage';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import CreateProject from './pages/CreateProject';
import CustomFonts from './pages/CustomFonts';
import ApiTokensPage from './pages/ApiTokensPage';
import ApiDocumentation from './pages/ApiDocumentation';
import CreditsPage from './pages/CreditsPage';
import PaymentWaitingPage from './pages/PaymentWaitingPage';
import LandingPage from './pages/LandingPage';
import TermsPage from './pages/TermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const { t } = useLanguage();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center">
        <div className="text-white text-xl">{t('common.loading')}</div>
      </div>
    );
  }
  
  return user ? <>{children}</> : <Navigate to="/landing" />;
};

// Font Cache Loader Component
const FontCacheLoader = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    const loadFonts = async () => {
      try {
        // Initialize font cache system
        
        // First, inject any already cached fonts immediately
        const cachedFonts = getCachedFonts();
        if (cachedFonts.length > 0) {
          injectCachedFonts(cachedFonts);
        }

        // Then fetch latest fonts from Supabase
        const { data: customFonts } = await supabase
          .from('custom_fonts')
          .select('id, font_name, font_file_url, font_family_css')
          .eq('user_id', user.id);

        if (customFonts && customFonts.length > 0) {
          // Load and cache fonts (downloads only new ones)
          const allCachedFonts = await loadAndCacheUserFonts(user.id, customFonts);
          
          // Inject all fonts into document
          injectCachedFonts(allCachedFonts);
          
          // Font cache system ready
        }
      } catch (err) {
        console.error('Error loading fonts:', err);
      }
    };

    loadFonts();
  }, [user]);

  return <>{children}</>;
};

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <FontCacheLoader>
          <Routes>
                {/* Public Routes */}
                <Route path="/landing" element={<LandingPage />} />
                <Route path="/signin" element={<SignIn />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/api-docs" element={<ApiDocumentation />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
                
                {/* Protected Routes */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <HomePage />
                  </ProtectedRoute>
                } />
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                <Route path="/create-project" element={
                  <ProtectedRoute>
                    <CreateProject />
                  </ProtectedRoute>
                } />
                <Route path="/custom-fonts" element={
                  <ProtectedRoute>
                    <CustomFonts />
                  </ProtectedRoute>
                } />
                <Route path="/editor" element={
                  <ProtectedRoute>
                    <EditorProvider>
                      <EditorPage />
                    </EditorProvider>
                  </ProtectedRoute>
                } />
                <Route path="/runner" element={
                  <ProtectedRoute>
                    <RunnerPage />
                  </ProtectedRoute>
                } />
                <Route path="/api-tokens" element={
                  <ProtectedRoute>
                    <ApiTokensPage />
                  </ProtectedRoute>
                } />
                <Route path="/credits" element={
                  <ProtectedRoute>
                    <CreditsPage />
                  </ProtectedRoute>
                } />
                <Route path="/payment-waiting" element={
                  <ProtectedRoute>
                    <PaymentWaitingPage />
                  </ProtectedRoute>
                } />
                <Route path="/account" element={
                  <ProtectedRoute>
                    <AccountPage />
                  </ProtectedRoute>
                } />
                <Route path="/admin" element={
                  <ProtectedRoute>
                    <AdminPage />
                  </ProtectedRoute>
                } />
          </Routes>
          </FontCacheLoader>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
